import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS, PageType } from 'types';
import type { ProductListItemInternal } from 'types/capi/shared';

import CarouselScroller from '~/components/CarouselScroller';
import NotificationTooltip from '~/components/NotificationTooltip';
import ProductCard from '~/components/ProductCard';
import Tracked from '~/components/Tracked';
import useHasScrolledIntoView from '~/hooks/useHasScrolledIntoView';

const PRODUCTS_PER_DESKTOP_ROW = 4;

type Props = {
  allowNotifications: boolean;
  compact?: boolean;
  description: string;
  modulePosition?: number;
  moduleType: string;
  overrideInteractionPagePosition?: boolean;
  pagePosition: PAGE_POSITIONS;
  pageType: PageType;
  products: ProductListItemInternal[];
  showBackground: boolean;
  title: string;
  viewMoreUrl?: string;
};

export default function PopularReleasesCarousel({
  allowNotifications,
  compact = false,
  description,
  modulePosition,
  moduleType,
  overrideInteractionPagePosition = false,
  pagePosition,
  pageType,
  products,
  showBackground,
  title,
  viewMoreUrl
}: Props) {
  const { t } = useTranslation('common');
  const productsRef = useRef<HTMLDivElement>(null);
  const productsInView = useHasScrolledIntoView(productsRef);

  return (
    <div
      className={`w-full ${showBackground ? `bg-black text-white ${compact ? 'py-8 desktop:py-10' : 'py-6 md:py-10'}` : ''}`}
    >
      <div
        className={`content-width flex h-fit flex-col items-center justify-start gap-6 ${pageType === 'plp' ? '' : 'md:gap-10'}`}
      >
        <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-2'}`}>
            <span
              className={
                compact
                  ? 'text-title-sm'
                  : pageType === 'plp'
                    ? 'text-title-md leading-none'
                    : 'text-title-xl'
              }
            >
              {title}
            </span>
            <span className="text-card-sm">{description}</span>
          </div>
          {allowNotifications && (
            <NotificationTooltip
              className={`w-full shrink-0 xs:w-[42vw] md:w-[264px] ${products.length < PRODUCTS_PER_DESKTOP_ROW ? 'md:hidden' : ''}`}
              isRevealed={productsInView}
            />
          )}
        </div>
        <div className="w-full" ref={productsRef}>
          <CarouselScroller
            slidesClassName={compact ? 'gap-3' : 'gap-2 md:gap-6'}
          >
            {products.map((product, productIndex) => (
              <ProductCard.Internal
                key={`product-${productIndex}`}
                product={product}
                padImage
                className={`w-[42vw] flex-none ${compact ? 'md:w-[calc((100%-2.25rem)/4)]' : 'md:w-[264px]'}`}
                allowNotifications={allowNotifications}
                pageType={pageType}
                imageLoading="eager"
                cardSize="small"
                modulePosition={modulePosition}
                moduleType={moduleType}
                showNotificationCount={allowNotifications ? 'always' : true}
                pagePosition={pagePosition}
                position={productIndex + 1}
                overrideInteractionPagePosition={
                  overrideInteractionPagePosition
                }
              />
            ))}
          </CarouselScroller>
        </div>
        {viewMoreUrl && (
          <div className="w-36">
            <Tracked.InternalLink
              variant={
                showBackground
                  ? 'transparent-white-border'
                  : 'transparent-black-border'
              }
              className="flex items-center justify-center"
              pagePosition={pagePosition}
              name="VIEW_MORE"
              to={viewMoreUrl}
            >
              {t('viewMore')}
            </Tracked.InternalLink>
          </div>
        )}
      </div>
    </div>
  );
}
